import classNames from "classnames";
import type { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { templates } from "@reactivated";

import { STAR_SETS, StarSVG } from "../StarRating";

/**
 * Renders the ``FlagshipIndexReviews`` Orderable rows, NOT the
 * FlagshipPage ``FlagshipReviewsBlock`` (that one nests its stars bare under
 * ``.reviews__header`` and wraps pullquotes in ``.reviews__quote``; this one
 * uses ``.star-rating.reviews__rating > .star-rating__stars``
 * wrappers and a bare ``p.type_x1point5`` pullquote).
 *
 * Read-more split: the review list splits into a VISIBLE ``.reviews`` group
 * (reviews 1-2), a ``.reviews__readmore-toggle`` and a HIDDEN
 * ``.reviews__readmore-target`` group (reviews 3+); a site-wide
 * ``[data-section-toggle]`` DOMContentLoaded handler toggled ``--is-active``
 * on both. That global binding is gone (the full-document render made it a
 * double-bind hazard on React-owned nodes), so expand/collapse is component
 * state here; the markup (the ``data-section-toggle`` /
 * ``data-section-toggle-target`` attributes -- ``_general.scss`` keys
 * ``cursor: pointer`` on the former -- ``aria-expanded``, the
 * ``--is-active`` class semantics) is reproduced verbatim. With fewer than
 * 3 reviews no toggle/target is emitted.
 *
 * The section heading and the read-more label render ``page.title`` as raw
 * HTML (not escaped), reproduced via dangerouslySetInnerHTML; neither
 * string was translated.
 */

type ReviewItem = templates.FlagshipIndexTemplate["reviews"][number];

type IProps = Immutable<{
    reviews: ReviewItem[];
    pageTitle: string;
}>;

const ReviewCard = (
    props: Immutable<{ item: ReviewItem }>,
): React.JSX.Element => {
    const item = props.item;
    // Named `rating` so the extracted msgid matches the existing
    // "Rated ${ rating } out of 5 stars" string (ttag keys collide otherwise).
    const rating = item.rating;
    const stars = rating ? STAR_SETS[rating] : undefined;
    return (
        <div className="reviews__review">
            <div className="reviews__header">
                <div className="star-rating reviews__rating">
                    <div className="star-rating__stars">
                        {stars
                            ? [
                                  <span
                                      className="ada-screenreader-only"
                                      key="label"
                                  >
                                      {t`Rated ${rating} out of 5 stars`}
                                  </span>,
                                  ...stars.map((path, i) => (
                                      <StarSVG key={i} path={path} />
                                  )),
                              ]
                            : null}
                    </div>
                </div>
            </div>
            <div className="reviews__body">
                {item.pullquote ? (
                    <p className="type_x1point5">
                        {"“"}
                        {item.pullquote}
                        {"”"}
                    </p>
                ) : null}
                {item.quote ? (
                    <div
                        className="reviews__copy"
                        dangerouslySetInnerHTML={{ __html: item.quote }}
                    />
                ) : null}
            </div>
            <div className="reviews__footer">
                {item.author ? (
                    <div className="reviews__author">{item.author}</div>
                ) : null}
                {item.city_state ? (
                    <div className="reviews__location">{item.city_state}</div>
                ) : null}
            </div>
        </div>
    );
};

export const FlagshipIndexReviews = (
    props: IProps,
): React.JSX.Element | null => {
    const [expanded, setExpanded] = React.useState(false);
    if (props.reviews.length === 0) {
        return null;
    }

    // Split at the 3rd review: reviews 1-2 always visible, reviews 3+ in
    // the read-more target.
    const visible = props.reviews.slice(0, 2);
    const hidden = props.reviews.slice(2);

    const onToggle = (event: React.SyntheticEvent) => {
        event.preventDefault();
        setExpanded((prev) => !prev);
    };

    const onToggleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setExpanded((prev) => !prev);
        }
    };

    // Hoisted for the ttag templates below (member expressions in `t`
    // interpolations break `ttag update` extraction).
    const pageTitle = props.pageTitle;

    return (
        <div
            className="reviews-container l-capped-width  u-margin-bottom--xl"
            id="reviews"
        >
            <div className="lead-in">
                <h4
                    dangerouslySetInnerHTML={{
                        __html: t`${pageTitle} Reviews`,
                    }}
                />
            </div>
            <div className="reviews">
                {visible.map((item, i) => (
                    <ReviewCard key={i} item={item} />
                ))}
            </div>
            {hidden.length > 0 ? (
                <>
                    <div
                        className={classNames(
                            "reviews__readmore-toggle",
                            "l-capped-width",
                            {
                                "reviews__readmore-toggle--is-active": expanded,
                            },
                        )}
                        role="button"
                        aria-expanded={expanded ? "true" : "false"}
                        tabIndex={0}
                        data-section-toggle
                        onClick={onToggle}
                        onKeyDown={onToggleKeyDown}
                    >
                        <div
                            className="reviews__readmore"
                            dangerouslySetInnerHTML={{
                                __html:
                                    t`More ${pageTitle} Reviews` +
                                    '<br><span class="reviews__readmore-arrow"></span>',
                            }}
                        />
                    </div>
                    <div
                        className={classNames(
                            "reviews__readmore-target",
                            "reviews",
                            {
                                "reviews__readmore-target--is-active": expanded,
                            },
                        )}
                        data-section-toggle-target
                    >
                        {hidden.map((item, i) => (
                            <ReviewCard key={i} item={item} />
                        ))}
                    </div>
                </>
            ) : null}
        </div>
    );
};
